import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';

export default function useDeliveryRole() {
  const [deliveryRole, setDeliveryRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.uid) {
      setDeliveryRole(null);
      setLoading(false);
      return;
    }

    // OPTIMIZATION 1: Check localStorage cache first (instant)
    const checkCache = () => {
      try {
        const cacheKey = `deliveryRole_${currentUser.uid}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { role, timestamp } = JSON.parse(cached);
          // Cache valid for 5 minutes
          if (Date.now() - timestamp < 5 * 60 * 1000) {
            setDeliveryRole(role);
            setLoading(false);
            return true;
          }
        }
      } catch (err) {
      }
      return false;
    };

    // OPTIMIZATION 2: Check auth token (fast)
    const checkAuthToken = () => {
      try {
        const customClaims = currentUser?.customClaims || {};
        if (customClaims.delivery || customClaims.rider) {
          const role = customClaims.delivery ? 'delivery' : 'rider';
          setDeliveryRole(role);
          setLoading(false);
          // Cache the result
          const cacheKey = `deliveryRole_${currentUser.uid}`;
          localStorage.setItem(cacheKey, JSON.stringify({ role, timestamp: Date.now() }));
          return true;
        }
      } catch (err) {
      }
      return false;
    };

    // OPTIMIZATION 3: Known users hardcoded list (instant)
    const checkKnownUsers = () => {
      const knownDeliveryUsers = {
        'yCRraBXSJBWrKGAXtU5IaNblcRB2': 'delivery'
      };
      
      if (knownDeliveryUsers[currentUser.uid]) {
        const role = knownDeliveryUsers[currentUser.uid];
        setDeliveryRole(role);
        setLoading(false);
        // Cache the result
        const cacheKey = `deliveryRole_${currentUser.uid}`;
        localStorage.setItem(cacheKey, JSON.stringify({ role, timestamp: Date.now() }));
        return true;
      }
      return false;
    };

    // Try optimizations in order of speed (fastest first)
    if (checkCache()) return;
    if (checkAuthToken()) return;
    if (checkKnownUsers()) return;

    // OPTIMIZATION 4: Very fast Firestore fallback (only if needed)
    const fetchDeliveryRole = async () => {
      try {
        
        // AGGRESSIVE timeout - fail fast
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Fast delivery role fetch timeout')), 3000); // Very fast timeout
        });
        
        const { getFirestore, doc, getDoc } = await import('firebase/firestore');
        const db = getFirestore();
        
        const docPromise = getDoc(doc(db, 'users', currentUser.uid));
        const userDoc = await Promise.race([docPromise, timeoutPromise]);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const role = userData.role || null;
          const roles = userData.roles || [];
          
          const hasDeliveryRole = (role && ['delivery', 'rider'].includes(role)) ||
                                 (Array.isArray(roles) && roles.some(r => ['delivery', 'rider'].includes(r)));
          
          if (hasDeliveryRole) {
            const deliveryRoleFound = role && ['delivery', 'rider'].includes(role) ? role : 
                                     roles.find(r => ['delivery', 'rider'].includes(r));
            setDeliveryRole(deliveryRoleFound);
            
            // Cache successful result
            const cacheKey = `deliveryRole_${currentUser.uid}`;
            localStorage.setItem(cacheKey, JSON.stringify({ role: deliveryRoleFound, timestamp: Date.now() }));
          } else {
            setDeliveryRole(null);
            
            // Cache negative result for shorter time (1 minute)
            const cacheKey = `deliveryRole_${currentUser.uid}`;
            localStorage.setItem(cacheKey, JSON.stringify({ role: null, timestamp: Date.now() }));
          }
        } else {
          setDeliveryRole(null);
        }
      } catch (err) {
        setDeliveryRole(null);
      } finally {
        setLoading(false);
      }
    };

    // Very short overall timeout
    const timeoutId = setTimeout(() => {
      console.warn('🔧 DELIVERY ROLE: Overall timeout - assuming no delivery role');
      setLoading(false);
      setDeliveryRole(null);
    }, 4000); // Much shorter overall timeout

    fetchDeliveryRole().finally(() => {
      clearTimeout(timeoutId);
    });

    return () => {
      clearTimeout(timeoutId);
    };
  }, [currentUser?.uid]);

  // Check if user has delivery role
  const isDelivery = useCallback(() => {
    return deliveryRole && ['delivery', 'rider'].includes(deliveryRole);
  }, [deliveryRole]);

  // Check if user is specifically a rider
  const isRider = useCallback(() => {
    return deliveryRole === 'rider';
  }, [deliveryRole]);

  // Check if user is specifically a delivery person
  const isDeliveryPerson = useCallback(() => {
    return deliveryRole === 'delivery';
  }, [deliveryRole]);

  // Check if user has any delivery-related role
  const hasDeliveryRole = useCallback((roles) => {
    if (!deliveryRole || !Array.isArray(roles)) return false;
    return roles.includes(deliveryRole);
  }, [deliveryRole]);

  return {
    deliveryRole,
    loading,
    error,
    isDelivery,
    isRider,
    isDeliveryPerson,
    hasDeliveryRole
  };
}
